import { useEffect, useState, type ReactNode } from "react";
import { WestyMark } from "./ui";
import { AuthModal } from "./AuthModal";
import { useUI } from "../lib/theme";
import pkg from "../../package.json";

/** Correo de contacto público (provisional, mientras no haya buzón de soporte). */
const CONTACT_EMAIL = "rmontestt@gmail.com";

/** Punto de transparencia: icono + texto. Concreto, para generar confianza antes de conectar. */
function TrustPoint({ icon, title, children }: { icon: string; title: string; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span aria-hidden className="text-lg leading-6">
        {icon}
      </span>
      <span className="text-sm text-muted">
        <span className="font-semibold text-fg">{title}</span> {children}
      </span>
    </li>
  );
}

/** Tarjeta de pilar (Performance / Salud / IA). */
function Pillar({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="rounded-2xl border border-line bg-panel/60 p-6 text-left transition hover:border-accent/50">
      <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-2xl">
        <span aria-hidden>{icon}</span>
      </div>
      <h3 className="mb-1 text-lg font-bold text-fg">{title}</h3>
      <p className="text-sm leading-relaxed text-muted">{desc}</p>
    </div>
  );
}

/** Paso numerado de "Cómo funciona". */
function Step({ n, title, desc }: { n: number; title: string; desc: string }) {
  return (
    <div className="text-left">
      <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-accent text-sm font-bold text-white">
        {n}
      </div>
      <h3 className="mb-1 font-bold text-fg">{title}</h3>
      <p className="text-sm leading-relaxed text-muted">{desc}</p>
    </div>
  );
}

/** Tarjeta de plan de precios. Hoy son placeholders (CTA sin destino real). */
function PlanCard({
  name,
  price,
  desc,
  cta,
  highlight,
}: {
  name: string;
  price: string;
  desc: string;
  cta: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex flex-col rounded-2xl border p-6 text-left ${
        highlight ? "border-accent bg-panel/80 shadow-lg shadow-accent/10" : "border-line bg-panel/40"
      }`}
    >
      <h3 className="text-sm font-bold uppercase tracking-wide text-muted">{name}</h3>
      <p className="mt-2 font-display text-2xl font-bold text-fg">{price}</p>
      <p className="mt-2 flex-1 text-sm text-muted">{desc}</p>
      {/* Placeholder: el destino real (checkout/onboarding) se conectará más adelante. */}
      <button
        type="button"
        className={`mt-5 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
          highlight
            ? "bg-accent text-white hover:brightness-110"
            : "border border-line text-fg hover:bg-panel"
        }`}
      >
        {cta}
      </button>
    </div>
  );
}

export function Landing({ onAuthSuccess, onNeedsConnection, onDemo }: {
  onAuthSuccess?: () => void;
  onNeedsConnection?: () => void;
  onDemo?: () => void;
} = {}) {
  const { t, theme, setTheme, lang, setLang } = useUI();
  const year = new Date().getFullYear();
  const [gateOpen, setGateOpen] = useState(false);
  const inviteFromUrl = new URLSearchParams(window.location.search).get("invite")?.trim().toUpperCase() ?? "";
  useEffect(() => { if (inviteFromUrl) setGateOpen(true); }, [inviteFromUrl]);

  // FAQ: la pregunta de datos se responde con la lista tabulada (icono + título + texto),
  // mismo formato de confianza de siempre; el resto son respuestas de texto.
  const faqs: { q: string; a?: string; content?: ReactNode }[] = [
    { q: t("lp_faq_q1"), a: t("lp_faq_a1") },
    { q: t("lp_faq_q2"), a: t("lp_faq_a2") },
    {
      q: t("lp_faq_q3"),
      content: (
        <ul className="mt-3 space-y-3">
          <TrustPoint icon="🔒" title={t("lp_trust_read_t")}>
            {t("lp_trust_read_d")}
          </TrustPoint>
          <TrustPoint icon="🗄️" title={t("lp_trust_store_t")}>
            {t("lp_trust_store_d")}
          </TrustPoint>
          <TrustPoint icon="🚫" title={t("lp_trust_sell_t")}>
            {t("lp_trust_sell_d")}
          </TrustPoint>
          <TrustPoint icon="🗑️" title={t("lp_trust_control_t")}>
            {t("lp_trust_control_d")}
          </TrustPoint>
        </ul>
      ),
    },
    { q: t("lp_faq_q4"), a: t("lp_faq_a4") },
  ];

  return (
    <div id="top" className="min-h-[100dvh]">
      {/* ───────────── Nav ───────────── */}
      <header className="sticky top-0 z-30 border-b border-line bg-ink/80 backdrop-blur">
        <nav className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <a href="#top" className="flex items-center gap-2">
            <WestyMark className="h-7 w-7" />
            <span className="text-lg font-extrabold tracking-tight text-accent">Westive</span>
          </a>
          <div className="hidden items-center gap-7 text-sm font-medium text-muted md:flex">
            <a href="#features" className="transition hover:text-fg">
              {t("lp_nav_features")}
            </a>
            <a href="#entrenadores" className="transition hover:text-fg">
              {t("lp_nav_coaches")}
            </a>
            <a href="#precios" className="transition hover:text-fg">
              {t("lp_nav_pricing")}
            </a>
            <a href="#faq" className="transition hover:text-fg">
              {t("lp_nav_faq")}
            </a>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label={t("lp_theme_aria")}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-sm transition hover:bg-panel"
            >
              {theme === "dark" ? "☀️" : "🌙"}
            </button>
            <button
              type="button"
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              aria-label={t("lp_lang_aria")}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-xs font-bold text-muted transition hover:bg-panel hover:text-fg"
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
            <button
              type="button"
              onClick={() => setGateOpen(true)}
              className="rounded-lg bg-accent px-3 py-1.5 text-sm font-semibold text-white transition hover:brightness-110"
            >
              {t("lp_signin")}
            </button>
          </div>
        </nav>
      </header>

      <main>
        {/* ───────────── Hero ───────────── */}
        <section className="mx-auto flex max-w-3xl flex-col items-center gap-5 px-4 py-20 text-center sm:py-28">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-panel/60 px-3 py-1 text-xs font-medium text-muted">
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-accent" />
            {t("lp_badge")}
          </span>
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-6xl">{t("lp_tagline")}</h1>
          <div className="mt-2 flex flex-col items-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => setGateOpen(true)}
              className="inline-flex items-center justify-center rounded-xl bg-accent px-6 py-3 text-base font-semibold text-white transition hover:brightness-110"
            >
              {t("lp_cta")}
            </button>
            <button
              type="button"
              onClick={onDemo}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-line px-6 py-3 text-base font-semibold text-fg transition hover:bg-panel"
            >
              {t("lp_cta_secondary")}
            </button>
          </div>
          <p className="text-xs text-muted">{t("lp_cta_note")}</p>
        </section>

        {/* ───────────── Pilares / Producto ───────────── */}
        <section id="features" className="scroll-mt-20 border-t border-line bg-panel/20">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
            <div className="mb-10 text-center">
              <p className="text-sm font-semibold uppercase tracking-wide text-accent">
                {t("lp_features_eyebrow")}
              </p>
              <h2 className="mt-1 text-2xl font-extrabold tracking-tight sm:text-3xl">
                {t("lp_features_title")}
              </h2>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              <Pillar icon="📊" title={t("lp_pillar_perf_t")} desc={t("lp_pillar_perf_d")} />
              <Pillar icon="❤️" title={t("lp_pillar_health_t")} desc={t("lp_pillar_health_d")} />
              <Pillar icon="🤖" title={t("lp_pillar_ai_t")} desc={t("lp_pillar_ai_d")} />
            </div>
          </div>
        </section>

        {/* ───────────── Cómo funciona ───────────── */}
        <section id="como" className="scroll-mt-20">
          <div className="mx-auto max-w-5xl px-4 py-16 sm:py-20">
            <div className="mb-10 text-center">
              <p className="text-sm font-semibold uppercase tracking-wide text-accent">
                {t("lp_how_eyebrow")}
              </p>
              <h2 className="mt-1 text-2xl font-extrabold tracking-tight sm:text-3xl">
                {t("lp_how_title")}
              </h2>
            </div>
            <div className="grid gap-8 sm:grid-cols-3">
              <Step n={1} title={t("lp_step1_t")} desc={t("lp_step1_d")} />
              <Step n={2} title={t("lp_step2_t")} desc={t("lp_step2_d")} />
              <Step n={3} title={t("lp_step3_t")} desc={t("lp_step3_d")} />
            </div>
          </div>
        </section>

        {/* ───────────── Entrenadores ───────────── */}
        <section id="entrenadores" className="scroll-mt-20 border-t border-line bg-panel/20">
          <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:py-20 md:grid-cols-2 md:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-accent">
                {t("lp_coach_eyebrow")}
              </p>
              <h2 className="mt-1 text-2xl font-extrabold tracking-tight sm:text-3xl">
                {t("lp_coach_title")}
              </h2>
              <p className="mt-3 leading-relaxed text-muted">{t("lp_coach_desc")}</p>
              {/* Placeholder: captación de entrenadores; aún sin destino. */}
              <button
                type="button"
                className="mt-6 inline-flex items-center gap-2 rounded-xl border border-line px-5 py-2.5 text-sm font-semibold text-fg transition hover:bg-panel"
              >
                {t("lp_coach_cta")}
                <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-bold uppercase text-accent">
                  {t("lp_soon")}
                </span>
              </button>
            </div>
            <ul className="space-y-3 rounded-2xl border border-line bg-panel/50 p-6">
              {[t("lp_coach_b1"), t("lp_coach_b2"), t("lp_coach_b3")].map((b) => (
                <li key={b} className="flex gap-3 text-sm text-fg">
                  <span aria-hidden className="text-accent">
                    ✓
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ───────────── Precios ───────────── */}
        <section id="precios" className="scroll-mt-20">
          <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:py-20">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">
              {t("lp_pricing_eyebrow")}
            </p>
            <h2 className="mt-1 text-2xl font-extrabold tracking-tight sm:text-3xl">
              {t("lp_pricing_title")}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-muted">{t("lp_pricing_desc")}</p>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <PlanCard
                name={t("lp_plan_athlete_t")}
                price={t("lp_plan_athlete_price")}
                desc={t("lp_plan_athlete_d")}
                cta={t("lp_plan_cta")}
                highlight
              />
              <PlanCard
                name={t("lp_plan_coach_t")}
                price={t("lp_plan_coach_price")}
                desc={t("lp_plan_coach_d")}
                cta={t("lp_plan_cta_soon")}
              />
            </div>
          </div>
        </section>

        {/* ───────────── FAQ ───────────── */}
        <section id="faq" className="scroll-mt-20 border-t border-line bg-panel/20">
          <div className="mx-auto max-w-3xl px-4 py-16 sm:py-20">
            <div className="mb-8 text-center">
              <p className="text-sm font-semibold uppercase tracking-wide text-accent">
                {t("lp_faq_eyebrow")}
              </p>
              <h2 className="mt-1 text-2xl font-extrabold tracking-tight sm:text-3xl">
                {t("lp_faq_title")}
              </h2>
            </div>
            <div className="space-y-3">
              {faqs.map(({ q, a, content }) => (
                <details key={q} className="group rounded-xl border border-line bg-panel/50 p-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between font-semibold text-fg">
                    {q}
                    <span aria-hidden className="text-muted transition group-open:rotate-180">
                      ⌄
                    </span>
                  </summary>
                  {content ?? <p className="mt-2 text-sm leading-relaxed text-muted">{a}</p>}
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* ───────────── Footer ───────────── */}
      <footer className="border-t border-line bg-panel/20">
        <div className="mx-auto max-w-6xl px-4 py-12">
          <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-4">
            <div className="sm:col-span-2 md:col-span-1">
              <div className="flex items-center gap-2">
                <WestyMark className="h-7 w-7" />
                <span className="text-lg font-extrabold tracking-tight text-accent">Westive</span>
              </div>
              <p className="mt-3 text-sm text-muted">{t("lp_footer_tagline")}</p>
            </div>
            <FooterCol title={t("lp_footer_col_product")}>
              <FooterLink href="#features">{t("lp_link_features")}</FooterLink>
              <FooterLink href="#precios">{t("lp_nav_pricing")}</FooterLink>
              <FooterLink href="#faq">{t("lp_nav_faq")}</FooterLink>
              <button onClick={onDemo} className="text-left text-sm text-muted transition hover:text-fg">{t("lp_link_demo")}</button>
            </FooterCol>
            <FooterCol title={t("lp_footer_col_company")}>
              <FooterLink href="#entrenadores">{t("lp_nav_coaches")}</FooterLink>
              <FooterLink href="#">{t("lp_link_about")}</FooterLink>
              <FooterLink href={`mailto:${CONTACT_EMAIL}`}>{t("lp_link_contact")}</FooterLink>
            </FooterCol>
            <FooterCol title={t("lp_footer_col_legal")}>
              <FooterLink href="/privacidad">{t("lp_link_privacy")}</FooterLink>
              <FooterLink href="/terminos">{t("lp_link_terms")}</FooterLink>
            </FooterCol>
          </div>
          <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-line pt-6 text-xs text-muted sm:flex-row">
            <p>
              © {year} Westive · {t("lp_footer_beta")}
            </p>
            <span className="rounded-md border border-line bg-panel2 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none text-muted">
              Beta(v{pkg.version})
            </span>
          </div>
        </div>
      </footer>

      <AuthModal
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        onSuccess={() => { if (onAuthSuccess) onAuthSuccess(); else location.href = "/app"; }}
        onNeedsConnection={() => { if (onNeedsConnection) onNeedsConnection(); else location.href = "/app?onboarding=1"; }}
        prefillCode={inviteFromUrl || undefined}
      />
    </div>
  );
}

function FooterCol({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-fg">{title}</h3>
      <ul className="space-y-2">{children}</ul>
    </div>
  );
}

function FooterLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <li>
      <a href={href} className="text-sm text-muted transition hover:text-fg">
        {children}
      </a>
    </li>
  );
}
